import type { AuthoredBuildingPassageSpec } from "../../config/buildingPassages.ts";
import type { LinearRing, PolygonWithHoles } from "../geometry.ts";

export interface BuildingPassageSpan {
  readonly from: Readonly<{ x: number; z: number }>;
  readonly to: Readonly<{ x: number; z: number }>;
  readonly lengthM: number;
}

export interface WallOpeningInterval {
  readonly startM: number;
  readonly endM: number;
  readonly ceilingOffsetFromBuildingBaseM: number;
}

interface SegmentIntersection {
  readonly firstSegmentRatio: number;
  readonly secondSegmentRatio: number;
  readonly point: Readonly<{ x: number; z: number }>;
}

export function buildingPassageSpanWithinFootprint(
  footprint: Readonly<PolygonWithHoles>,
  passage: Readonly<AuthoredBuildingPassageSpec>,
): BuildingPassageSpan | null {
  const outerIntersections = [
    ...lineRingIntersections(
      passage.centreLineLocalM.from,
      passage.centreLineLocalM.to,
      footprint.outer,
    ),
  ].sort((left, right) =>
    left.firstSegmentRatio - right.firstSegmentRatio
  );
  const destinationHole = passage.destinationHoleIndex === undefined
    ? undefined
    : footprint.holes[passage.destinationHoleIndex];
  const intersections = destinationHole === undefined
    ? outerIntersections
    : [
        ...outerIntersections,
        ...lineRingIntersections(
          passage.centreLineLocalM.from,
          passage.centreLineLocalM.to,
          destinationHole,
        ),
      ].sort((left, right) => left.firstSegmentRatio - right.firstSegmentRatio);
  const first = intersections[0];
  const last = intersections.at(-1);
  if (
    first === undefined ||
    last === undefined ||
    intersections.length < 2
  ) {
    return null;
  }
  const lengthM = Math.hypot(
    last.point.x - first.point.x,
    last.point.z - first.point.z,
  );
  return lengthM <= 0.01
    ? null
    : {
        from: first.point,
        to: last.point,
        lengthM,
      };
}

export function passagesOpeningFootprintRing(
  passages: readonly Readonly<AuthoredBuildingPassageSpec>[],
  holeIndex: number | null,
): readonly Readonly<AuthoredBuildingPassageSpec>[] {
  return passages.filter((passage) =>
    holeIndex === null
      ? true
      : passage.destinationHoleIndex === holeIndex
  );
}

export function wallOpeningIntervalsForPassages(
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  passages: readonly Readonly<AuthoredBuildingPassageSpec>[],
): readonly WallOpeningInterval[] {
  const edgeX = to.x - from.x;
  const edgeZ = to.z - from.z;
  const edgeLengthM = Math.hypot(edgeX, edgeZ);
  if (edgeLengthM <= 0.001) {
    return [];
  }
  const edgeDirection = {
    x: edgeX / edgeLengthM,
    z: edgeZ / edgeLengthM,
  };
  const intervals: WallOpeningInterval[] = [];
  for (const passage of passages) {
    const passageFrom = passage.centreLineLocalM.from;
    const passageTo = passage.centreLineLocalM.to;
    const intersection = segmentIntersection(
      from,
      to,
      passageFrom,
      passageTo,
    );
    if (intersection === null) {
      continue;
    }
    const passageX = passageTo.x - passageFrom.x;
    const passageZ = passageTo.z - passageFrom.z;
    const passageLengthM = Math.hypot(passageX, passageZ);
    if (passageLengthM <= 0.001) {
      continue;
    }
    const passageNormal = {
      x: -passageZ / passageLengthM,
      z: passageX / passageLengthM,
    };
    const lateralRate = Math.abs(
      edgeDirection.x * passageNormal.x +
        edgeDirection.z * passageNormal.z,
    );
    if (lateralRate <= 0.05) {
      continue;
    }
    const centreM = intersection.firstSegmentRatio * edgeLengthM;
    const halfSpanM = passage.widthM / (2 * lateralRate);
    const startM = Math.max(0, centreM - halfSpanM);
    const endM = Math.min(edgeLengthM, centreM + halfSpanM);
    if (endM - startM <= 0.01) {
      continue;
    }
    intervals.push({
      startM,
      endM,
      ceilingOffsetFromBuildingBaseM:
        passage.ceilingOffsetFromBuildingBaseM,
    });
  }
  return mergeOpeningIntervals(intervals);
}

function lineRingIntersections(
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  source: Readonly<LinearRing>,
): readonly SegmentIntersection[] {
  const ring = source[0]?.x === source.at(-1)?.x &&
      source[0]?.z === source.at(-1)?.z
    ? source
    : [...source, source[0]!];
  const intersections: SegmentIntersection[] = [];
  for (let index = 1; index < ring.length; index += 1) {
    const edgeFrom = ring[index - 1];
    const edgeTo = ring[index];
    if (edgeFrom === undefined || edgeTo === undefined) {
      continue;
    }
    const intersection = segmentIntersection(from, to, edgeFrom, edgeTo);
    if (
      intersection !== null &&
      !intersections.some((existing) =>
        Math.abs(
          existing.firstSegmentRatio - intersection.firstSegmentRatio,
        ) < 0.000_001
      )
    ) {
      intersections.push(intersection);
    }
  }
  return intersections;
}

function segmentIntersection(
  firstFrom: Readonly<{ x: number; z: number }>,
  firstTo: Readonly<{ x: number; z: number }>,
  secondFrom: Readonly<{ x: number; z: number }>,
  secondTo: Readonly<{ x: number; z: number }>,
): SegmentIntersection | null {
  const first = {
    x: firstTo.x - firstFrom.x,
    z: firstTo.z - firstFrom.z,
  };
  const second = {
    x: secondTo.x - secondFrom.x,
    z: secondTo.z - secondFrom.z,
  };
  const determinant = first.x * second.z - first.z * second.x;
  if (Math.abs(determinant) <= 0.000_001) {
    return null;
  }
  const delta = {
    x: secondFrom.x - firstFrom.x,
    z: secondFrom.z - firstFrom.z,
  };
  const firstSegmentRatio =
    (delta.x * second.z - delta.z * second.x) / determinant;
  const secondSegmentRatio =
    (delta.x * first.z - delta.z * first.x) / determinant;
  const epsilon = 0.000_001;
  if (
    firstSegmentRatio < -epsilon ||
    firstSegmentRatio > 1 + epsilon ||
    secondSegmentRatio < -epsilon ||
    secondSegmentRatio > 1 + epsilon
  ) {
    return null;
  }
  return {
    firstSegmentRatio,
    secondSegmentRatio,
    point: {
      x: firstFrom.x + first.x * firstSegmentRatio,
      z: firstFrom.z + first.z * firstSegmentRatio,
    },
  };
}

function mergeOpeningIntervals(
  source: readonly WallOpeningInterval[],
): readonly WallOpeningInterval[] {
  const sorted = [...source].sort((left, right) =>
    left.startM - right.startM
  );
  const result: WallOpeningInterval[] = [];
  for (const interval of sorted) {
    const previous = result.at(-1);
    if (previous === undefined || interval.startM > previous.endM + 0.001) {
      result.push(interval);
      continue;
    }
    result[result.length - 1] = {
      startM: previous.startM,
      endM: Math.max(previous.endM, interval.endM),
      ceilingOffsetFromBuildingBaseM: Math.max(
        previous.ceilingOffsetFromBuildingBaseM,
        interval.ceilingOffsetFromBuildingBaseM,
      ),
    };
  }
  return result;
}
